import React, { useState } from 'react';
import { X, User, Check, Sparkles, Shield, Key, ExternalLink } from 'lucide-react';
import { UserProfile } from '../types';
import { ANIME_AVATARS } from '../data/initialCatalog';
import { updateUserAvatar, updateUserPreferences } from '../lib/firebase';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile | null;
  onUserUpdated: (updatedUser: UserProfile) => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  onClose,
  user,
  onUserUpdated
}) => {
  if (!isOpen || !user) return null;

  const [displayName, setDisplayName] = useState(user.displayName || user.username);
  const [selectedAvatar, setSelectedAvatar] = useState(user.avatar || ANIME_AVATARS[0].url);
  const [subdlApiKey, setSubdlApiKey] = useState(() => {
    if (user.preferences?.subdlApiKey) return user.preferences.subdlApiKey;
    try {
      return localStorage.getItem('subdl_user_api_key') || '';
    } catch {
      return '';
    }
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanSubdlKey = subdlApiKey.trim();
      const updatedPreferences = {
        ...(user.preferences || {}),
        subdlApiKey: cleanSubdlKey
      };

      // Also persist to localStorage for instant client access and offline fallback
      try {
        if (cleanSubdlKey) {
          localStorage.setItem('subdl_user_api_key', cleanSubdlKey);
        } else {
          localStorage.removeItem('subdl_user_api_key');
        }
      } catch {}

      await updateUserAvatar(user.uid, selectedAvatar, displayName.trim());
      await updateUserPreferences(user.uid, updatedPreferences);

      const updated: UserProfile = {
        ...user,
        displayName: displayName.trim(),
        avatar: selectedAvatar,
        preferences: updatedPreferences
      };
      onUserUpdated(updated);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1200);
    } catch (err) {
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      id="user-profile-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-md bg-[#0d1017] border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl shadow-black text-left max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-amber-400 shrink-0">
            <img src={selectedAvatar} alt="Avatar" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
          </div>
          <div>
            <h2 className="font-display font-bold text-xl text-white">El teu Perfil AnimeCat</h2>
            <p className="text-xs text-slate-400 font-mono">@{user.username}</p>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">
              Nom Visible
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              className="w-full px-4 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-white text-sm focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* SubDL API Key setting */}
          <div className="p-3.5 rounded-2xl bg-white/[0.03] border border-white/10 space-y-2">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-sky-300">
                <Key className="w-3.5 h-3.5" />
                <span>Clau API SubDL (Subtítols)</span>
              </label>
              <a
                href="https://subdl.com/panel/api"
                target="_blank"
                rel="noreferrer"
                className="text-[10px] text-slate-400 hover:text-sky-300 flex items-center gap-1 transition-colors"
              >
                <span>Obtenir clau</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Configura la teva API Key de SubDL per descarregar automàticament subtítols en català, castellà i altres idiomes. Quedarà desada permanentment al teu compte.
            </p>
            <input
              type="text"
              value={subdlApiKey}
              onChange={(e) => setSubdlApiKey(e.target.value)}
              placeholder="Ex: SD-xxxxxxxxxxxxxxxxxxxx"
              className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-white text-xs font-mono focus:border-sky-400 focus:outline-none placeholder:text-slate-600"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-2">
              Canvia el teu Avatar
            </label>
            <div className="grid grid-cols-6 gap-2">
              {ANIME_AVATARS.map((avatar) => (
                <button
                  key={avatar.id}
                  type="button"
                  onClick={() => setSelectedAvatar(avatar.url)}
                  className={`relative rounded-xl overflow-hidden aspect-square border-2 transition-all ${
                    selectedAvatar === avatar.url 
                      ? 'border-amber-400 scale-105 shadow-md shadow-amber-500/30' 
                      : 'border-white/10 opacity-60 hover:opacity-100'
                  }`}
                  title={avatar.name}
                >
                  <img
                    src={avatar.url}
                    alt={avatar.name}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 transition-all mt-4"
          >
            {saving ? (
              <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : success ? (
              <>
                <Check className="w-4 h-4" />
                <span>Guardat correctament!</span>
              </>
            ) : (
              <span>Desa els Canvis</span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};
